import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, ChevronRight, Plus } from 'lucide-react';
import { adminQueries, getErrorMessage, hasProblemType, keys } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { formatNumber } from '@blimo/shared';
import { Badge, Button, Card, Checkbox, ConfirmDialog, EmptyState, ErrorState, Input, SearchInput, Skeleton, toast, ToggleGroup } from '@blimo/ui';
import { changeCount, sameSet } from './utils.js';

const VIEWS = [
  { value: 'relevant', label: 'Mit Zuordnung' },
  { value: 'all', label: 'Alle Liegenschaften' },
];

/**
 * @typedef {'units'|'accounts'} TreeMode
 * Mode-specific bits: which items a property row lists, which of them are selected on the server and how to save.
 */
const MODES = {
  units: {
    noun: 'Einheiten',
    countOf: (/** @type {any} */ p) => `${formatNumber(p.assigned_units_count)} von ${formatNumber(p.units_count)} zugeordnet`,
    relevant: (/** @type {any} */ p) => p.assigned_units_count > 0,
    items: (/** @type {any} */ state) => state.units.map((u) => ({ key: String(u.id), unit: u })),
    selected: (/** @type {any} */ state) => state.units.filter((u) => u.assigned).map((u) => String(u.id)),
    save: (api, clientId, propertyId, keys, options) => api.admin.mandates.syncUnits(clientId, propertyId, keys.map(Number), options),
    empty: 'Diese Liegenschaft hat keine Einheiten.',
  },
  accounts: {
    noun: 'Kostenkonten',
    countOf: (/** @type {any} */ p) => `${formatNumber(p.released_accounts_count)} freigegeben`,
    relevant: (/** @type {any} */ p) => p.released_accounts_count > 0 || p.assigned_units_count > 0,
    items: (/** @type {any} */ state) => state.cost_accounts.map((a) => ({ key: a.number, account: a })),
    selected: (/** @type {any} */ state) => state.cost_accounts.filter((a) => a.released).map((a) => a.number),
    save: (api, clientId, propertyId, keys) => api.admin.mandates.syncCostAccounts(clientId, propertyId, keys),
    empty: 'Für diese Liegenschaft sind keine Sachkonten bekannt.',
  },
};

/**
 * Liegenschaft → Einheiten bzw. Kostenkonten als aufklappbarer Baum mit Checkboxen. Änderungen werden lokal gesammelt
 * und gemeinsam gespeichert (je geänderter Liegenschaft ein idempotenter PUT). Ohne `canEdit` nur lesend.
 * @param {{ clientId: string, mode: TreeMode, canEdit: boolean }} props
 */
export function PropertyTree({ clientId, mode, canEdit }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const cfg = MODES[mode];
  const [q, setQ] = useState('');
  const [view, setView] = useState('relevant');
  const [expanded, setExpanded] = useState(/** @type {Set<number>} */ (new Set()));
  /** Selection drafts per property id (only properties the user touched). */
  const [drafts, setDrafts] = useState(/** @type {Record<number, string[]>} */ ({}));
  const [saving, setSaving] = useState(false);
  const { data, error, isPending, refetch } = useQuery(adminQueries.mandateProperties(api, clientId, { q: q.trim() || undefined }));

  const all = data ?? [];
  const relevantCount = all.filter(cfg.relevant).length;
  const effectiveView = view === 'relevant' && relevantCount === 0 && !q ? 'all' : view;
  const properties = effectiveView === 'relevant' ? all.filter((p) => cfg.relevant(p) || drafts[p.id]) : all;

  /** Server state of a property (cached by the row query). @param {number} propertyId */
  const serverState = (propertyId) => queryClient.getQueryData(adminQueries.mandateProperty(api, clientId, propertyId).queryKey);

  /** @param {number} propertyId @param {string[]} next */
  const setDraft = (propertyId, next) =>
    setDrafts((prev) => {
      const state = serverState(propertyId);
      const copy = { ...prev };
      if (state && sameSet(next, cfg.selected(state))) delete copy[propertyId];
      else copy[propertyId] = next;
      return copy;
    });

  const toggleExpanded = (/** @type {number} */ id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  /** Property checkbox: select all / none (loads the property first if needed). @param {any} property @param {boolean} checked */
  async function toggleProperty(property, checked) {
    try {
      const state = await queryClient.fetchQuery(adminQueries.mandateProperty(api, clientId, property.id));
      setDraft(property.id, checked ? state.units.filter((u) => !u.archived || u.assigned).map((u) => String(u.id)) : []);
      setExpanded((prev) => new Set(prev).add(property.id));
    } catch (e) {
      toast.error(getErrorMessage(e, 'Die Einheiten konnten nicht geladen werden.'));
    }
  }

  const [conflicts, setConflicts] = useState(/** @type {any[]|null} */ (null));
  const dirtyIds = Object.keys(drafts).map(Number);
  const changes = dirtyIds.reduce((sum, id) => {
    const state = serverState(id);
    return sum + (state ? changeCount(cfg.selected(state), drafts[id]) : 0);
  }, 0);

  /** @param {{ move?: boolean }} [options] `move`: Einheiten anderer Mandanten nach Rückfrage umhängen */
  async function save(options = {}) {
    setSaving(true);
    let saved = 0;
    try {
      for (const id of dirtyIds) {
        try {
          await cfg.save(api, clientId, id, drafts[id], options);
        } catch (e) {
          // Eine Einheit gehört genau einem Mandanten → Rückfrage statt Fehler
          if (hasProblemType(e, 'unit-assigned-to-other-client')) {
            setConflicts(/** @type {any} */ (e).problem?.conflicts ?? []);
            return;
          }
          throw e;
        }
        saved += 1;
        setDrafts((prev) => {
          const copy = { ...prev };
          delete copy[id];
          return copy;
        });
      }
      toast.success(mode === 'units' ? 'Einheiten-Zuordnung gespeichert' : 'Kostenkonten-Freigaben gespeichert');
    } catch (e) {
      toast.error(getErrorMessage(e, saved > 0 ? 'Nicht alle Änderungen konnten gespeichert werden.' : 'Die Änderungen konnten nicht gespeichert werden.'));
    } finally {
      await queryClient.invalidateQueries({ queryKey: keys.admin.resource('mandates') });
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <ConfirmDialog
        open={conflicts != null}
        onOpenChange={(open) => !open && setConflicts(null)}
        title="Einheiten anderem Mandanten zugeordnet"
        description={
          conflicts
            ? `Eine Einheit gehört immer genau einem Mandanten. Umhängen entfernt die Zuordnung beim bisherigen Mandanten: ${conflicts
                .map((c) => `${c.unit || `Einheit ${c.unit_id}`}${c.property ? ` (${c.property})` : ''} → bisher ${c.client ?? 'unbekannt'}`)
                .join('; ')}.`
            : undefined
        }
        confirmLabel="Umhängen"
        onConfirm={() => {
          setConflicts(null);
          save({ move: true });
        }}
      />
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <SearchInput label="Liegenschaft suchen" placeholder="Name oder Nummer …" value={q} onValueChange={setQ} />
        <ToggleGroup label="Liegenschaften anzeigen" value={effectiveView} onValueChange={(v) => v && setView(v)} options={VIEWS} />
      </div>
      {error ? (
        <ErrorState compact error={error} onRetry={() => refetch()} />
      ) : isPending ? (
        <Skeleton className="h-32 w-full" />
      ) : properties.length === 0 ? (
        <EmptyState
          compact
          icon={Building2}
          title={q ? 'Keine passende Liegenschaft' : 'Noch keine Zuordnung'}
          description={effectiveView === 'relevant' ? 'Wechseln Sie zu „Alle Liegenschaften“, um zuzuordnen.' : undefined}
        />
      ) : (
        <Card className="gap-0 py-0">
          <ul className="divide-y" aria-label={`Liegenschaften und ${cfg.noun}`}>
            {properties.map((property) => (
              <PropertyRow
                key={property.id}
                clientId={clientId}
                property={property}
                mode={mode}
                canEdit={canEdit}
                expanded={expanded.has(property.id)}
                onToggleExpanded={() => toggleExpanded(property.id)}
                draft={drafts[property.id]}
                onDraft={(next) => setDraft(property.id, next)}
                onToggleProperty={(checked) => toggleProperty(property, checked)}
              />
            ))}
          </ul>
        </Card>
      )}
      {canEdit && dirtyIds.length > 0 && (
        <div
          role="region"
          aria-label="Ungespeicherte Änderungen"
          className="sticky bottom-3 z-10 flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-card px-4 py-3 shadow-md"
        >
          <span className="text-sm">
            <span className="numeric font-medium">{formatNumber(changes)}</span> {changes === 1 ? 'Änderung' : 'Änderungen'} in{' '}
            <span className="numeric font-medium">{formatNumber(dirtyIds.length)}</span> {dirtyIds.length === 1 ? 'Liegenschaft' : 'Liegenschaften'}
          </span>
          <span className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setDrafts({})} disabled={saving}>
              Verwerfen
            </Button>
            <Button size="sm" onClick={() => save()} loading={saving}>
              Speichern
            </Button>
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * @param {{
 *   clientId: string, property: any, mode: TreeMode, canEdit: boolean, expanded: boolean, onToggleExpanded: () => void,
 *   draft?: string[], onDraft: (next: string[]) => void, onToggleProperty: (checked: boolean) => void,
 * }} props
 */
function PropertyRow({ clientId, property, mode, canEdit, expanded, onToggleExpanded, draft, onDraft, onToggleProperty }) {
  const api = useApi();
  const cfg = MODES[mode];
  const {
    data: state,
    error,
    isPending,
    refetch,
  } = useQuery({ ...adminQueries.mandateProperty(api, clientId, property.id), enabled: expanded || Boolean(draft) });
  const selected = draft ?? (state ? cfg.selected(state) : []);
  const regionId = `mandate-${mode}-${property.id}`;
  const label = [property.name, property.number && `(${property.number})`].filter(Boolean).join(' ');

  // Tri-state property checkbox (units only): exact once loaded, otherwise from the counts.
  const total = state ? state.units.length : property.units_count;
  const count = state || draft ? selected.length : property.assigned_units_count;
  const allChecked = total > 0 && count >= total;
  const someChecked = count > 0 && !allChecked;

  return (
    <li className={draft ? 'bg-accent/40' : undefined}>
      <div className="flex items-center gap-2 px-3 py-2">
        <Button
          variant="ghost"
          size="icon-sm"
          aria-expanded={expanded}
          aria-controls={regionId}
          aria-label={`${label} ${expanded ? 'zuklappen' : 'aufklappen'}`}
          onClick={onToggleExpanded}
        >
          <ChevronRight className={`size-4 transition-transform ${expanded ? 'rotate-90' : ''}`} aria-hidden="true" />
        </Button>
        {mode === 'units' && (
          <Checkbox
            aria-label={`Alle Einheiten von ${label}`}
            checked={allChecked}
            ref={(el) => {
              if (el) el.indeterminate = someChecked;
            }}
            disabled={!canEdit || total === 0}
            onChange={(e) => onToggleProperty(e.target.checked)}
          />
        )}
        <button type="button" className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 text-left text-sm" onClick={onToggleExpanded}>
          <span className="truncate font-medium">{property.name || `#${property.id}`}</span>
          {property.number && <span className="numeric text-xs text-muted-foreground">{property.number}</span>}
          {property.archived && <Badge variant="neutral">Archiviert</Badge>}
        </button>
        {draft && <Badge variant="warning">Geändert</Badge>}
        <span className="hidden text-xs text-muted-foreground sm:inline">{cfg.countOf(property)}</span>
      </div>
      {expanded && (
        <div id={regionId} className="border-t bg-muted/30 px-3 py-2 sm:pl-12">
          {error ? (
            <ErrorState compact error={error} onRetry={() => refetch()} />
          ) : isPending ? (
            <Skeleton className="h-16 w-full" />
          ) : (
            <ItemList mode={mode} state={state} selected={selected} canEdit={canEdit} onChange={onDraft} propertyLabel={label} />
          )}
        </div>
      )}
    </li>
  );
}

/** @param {{ mode: TreeMode, state: any, selected: string[], canEdit: boolean, onChange: (next: string[]) => void, propertyLabel: string }} props */
function ItemList({ mode, state, selected, canEdit, onChange, propertyLabel }) {
  const cfg = MODES[mode];
  const chosen = new Set(selected);
  const items = cfg.items(state);
  // Account numbers added by hand that the server does not know yet.
  const extra =
    mode === 'accounts'
      ? selected.filter((n) => !items.some((i) => i.key === n)).map((n) => ({ key: n, account: { number: n, name: null, in_ledger: false } }))
      : [];
  const all = [...items, ...extra];

  /** @param {string} key @param {boolean} on */
  const toggle = (key, on) => onChange(on ? [...selected, key] : selected.filter((k) => k !== key));

  return (
    <div className="flex flex-col gap-2">
      {all.length === 0 ? (
        <p className="text-sm text-muted-foreground">{cfg.empty}</p>
      ) : (
        <ul className="grid gap-1 sm:grid-cols-2 xl:grid-cols-3" aria-label={`${cfg.noun} von ${propertyLabel}`}>
          {all.map((item) => (
            <li key={item.key}>
              <label className="flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent has-[:disabled]:cursor-default">
                <Checkbox
                  className="mt-0.5"
                  checked={chosen.has(item.key)}
                  disabled={!canEdit}
                  onChange={(e) => toggle(item.key, e.target.checked)}
                  aria-label={item.unit ? unitLabel(item.unit) : `Konto ${item.account.number}${item.account.name ? ` ${item.account.name}` : ''}`}
                />
                {item.unit ? <UnitText unit={item.unit} /> : <AccountText account={item.account} />}
              </label>
            </li>
          ))}
        </ul>
      )}
      {mode === 'accounts' && canEdit && <AddAccount existing={all.map((i) => i.key)} onAdd={(n) => toggle(n, true)} propertyLabel={propertyLabel} />}
    </div>
  );
}

const unitLabel = (/** @type {any} */ unit) => `Einheit ${[unit.number, unit.designation].filter(Boolean).join(' · ')}`;

function UnitText({ unit }) {
  return (
    <span className="flex min-w-0 flex-col">
      <span>
        <span className="numeric font-medium">{unit.number}</span>
        {unit.designation && <span className="text-muted-foreground"> · {unit.designation}</span>}
      </span>
      <span className="flex flex-wrap gap-1">
        {unit.type && <span className="text-xs text-muted-foreground">{unit.type}</span>}
        {unit.archived && <Badge variant="neutral">Archiviert</Badge>}
        {unit.other_clients?.map((c) => (
          <Badge key={c.id} variant="warning" title="Die Einheit gehört diesem Mandanten; Auswählen hängt sie nach Rückfrage um">
            Mandant: {c.name}
          </Badge>
        ))}
      </span>
    </span>
  );
}

function AccountText({ account }) {
  return (
    <span className="flex min-w-0 flex-col">
      <span>
        <span className="numeric font-medium">{account.number}</span>
        <span className={account.name ? '' : 'text-muted-foreground italic'}> · {account.name || 'ohne Bezeichnung'}</span>
      </span>
      <span className="flex flex-wrap gap-1">
        {!account.in_ledger && <Badge variant="outline">nicht im Kontenplan</Badge>}
        {account.in_ledger && account.has_bookings === false && <span className="text-xs text-muted-foreground">keine Buchungen</span>}
      </span>
    </span>
  );
}

/** Manual account number (SE allowed free numbers; the API checks the format). */
function AddAccount({ existing, onAdd, propertyLabel }) {
  const [value, setValue] = useState('');
  const number = value.trim();
  const valid = /^\d{1,20}$/.test(number) && !existing.includes(number);

  return (
    <form
      className="flex items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid) return;
        onAdd(number);
        setValue('');
      }}
    >
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        inputMode="numeric"
        placeholder="Kontonummer"
        aria-label={`Weiteres Kostenkonto für ${propertyLabel}`}
        className="h-8 w-36"
      />
      <Button type="submit" size="sm" variant="outline" disabled={!valid}>
        <Plus aria-hidden="true" /> Konto freigeben
      </Button>
    </form>
  );
}
